import { Directive, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';

/**
 * Hybrid Data-Cy Directive
 * 1. Propagator: If placed on a parent with [data-cy], it finds internal inputs and names them.
 * 2. Climber: If placed on an input without data-cy, it searches upwards for a name.
 */
@Directive({
  // Added support for your custom components
  selector: '[data-cy], [uniqueId], input:not([data-cy]), textarea:not([data-cy]), select:not([data-cy]), mat-checkbox:not([data-cy]), mat-slide-toggle:not([data-cy]), mat-select:not([data-cy]), mat-option:not([data-cy]), mat-radio-button:not([data-cy]), app-generic-multiselect-checkbox:not([data-cy]), app-generic-multiselect-checkbox-option:not([data-cy]), app-add-connect-themes:not([data-cy])'
})
export class DataCyInputDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;

  constructor(private el: ElementRef<HTMLElement>) { }

  ngAfterViewInit(): void {
    this.apply();
    this.observer = new MutationObserver(() => this.apply());
    this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private apply(): void {
    const host = this.el.nativeElement;

    // Determine the base name for this element (prefer manual data-cy, then uniqueId, then placeholder)
    const baseCy = host.getAttribute('data-cy') ||
      host.getAttribute('uniqueId') ||
      host.getAttribute('placeholder')?.toLowerCase().replace(/\s+/g, '-');

    if (baseCy) {
      const targetQuery = 'input:not([data-cy]), textarea:not([data-cy]), select:not([data-cy]), mat-checkbox:not([data-cy]), mat-select:not([data-cy]), mat-option:not([data-cy]), app-generic-multiselect-checkbox-option:not([data-cy])';
      const targets = host.querySelectorAll<HTMLElement>(targetQuery);

      targets.forEach((target, i) => {
        const tag = target.tagName.toLowerCase().replace('mat-', '').replace('app-generic-multiselect-checkbox-', '');
        const suffix = i === 0 ? `__${tag}` : `__${tag}-${i + 1}`;
        target.setAttribute('data-cy', `${baseCy}${suffix}`);
      });
    }

    // Case 2: Climber (Look up for parent name)
    const parentWithCy = host.closest('[data-cy], [uniqueId]');
    if (parentWithCy && !host.hasAttribute('data-cy')) {
      const parentName = parentWithCy.getAttribute('data-cy') || parentWithCy.getAttribute('uniqueId');
      const tag = host.tagName.toLowerCase().replace('mat-', '').replace('app-generic-multiselect-checkbox-', '');
      const similar = Array.from(parentWithCy.querySelectorAll(host.tagName.toLowerCase()));
      const index = similar.indexOf(host);
      const suffix = index === 0 ? `__${tag}` : `__${tag}-${index + 1}`;
      host.setAttribute('data-cy', `${parentName}${suffix}`);
    }

    // Case 3: Intelligent labels for standalone options (in overlays)
    if (!host.hasAttribute('data-cy') && (host.tagName === 'MAT-OPTION' || host.tagName === 'APP-GENERIC-MULTISELECT-CHECKBOX-OPTION')) {
      const text = host.innerText.trim().toLowerCase().replace(/[^a-z0-9]/g, '-').substring(0, 25);
      if (text) host.setAttribute('data-cy', `option-${text}`);
    }
  }
}